import {
  array,
  boolean,
  type InferOutput,
  nullable,
  number,
  object,
  omit,
  string
} from 'valibot'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'
import {
  RoundTypePicklist,
  ShipmentOrderAdditionalFormValueSchema,
  shipmentOrderCardEntries,
  updateOnlyEntries,
  withExpenseTaxRequiredChecks,
  withIncomeTaxRequiredChecks,
  withTimeRestrictionChecks
} from '@/features/shipment-orders/models'
import { nonNullableDateToStringSchema } from '@/libs/valibot'

/** 矢羽根編集では指定パートナーを入力させない（依頼側ではなく矢羽根側で担うため） */
const cardEntriesWithoutPartner = omit(object(shipmentOrderCardEntries), [
  'partnerId'
]).entries

/**
 * 配送依頼1件分のスキーマ（矢羽根編集用）。
 *
 * 請求側の税区分・端数処理の必須チェックは含まない。複数矢羽根(group)モードは請求側も
 * 「未入力=既存値を変更しない」持ち回りのため、必須にすると空スタートの入力欄で保存が塞がる
 * （払い側の必須を root に置いて group の entries 流用から外している構造と対になる）。
 */
const shipmentItemSchema = withTimeRestrictionChecks(
  object({ ...cardEntriesWithoutPartner, ...updateOnlyEntries })
)

/**
 * 単一矢羽根モード用。請求側の税区分・端数処理を必須にする。
 * 金額の有無に関わらず必須。内税フラグは対象外（null=外税仕様）。
 */
const shipmentItemWithIncomeRequiredSchema =
  withIncomeTaxRequiredChecks(shipmentItemSchema)

/**
 * root 直下のフィールド（shipments を除く）。
 * single / group 両スキーマで共用する。
 */
const dispatchDetailRootEntries = {
  // --- DispatchDetail用 ---
  driverUserId: nullable(number()),
  driverName: nullable(string()),
  expense: nullable(number()),
  /**
   * #3328 (VOC_PJ-761): 払い運賃の案件個別税区分。
   * 編集モーダルではUIコントロールなし（値保持のみ）。
   */
  expenseTaxCategoryId: nullable(number()),
  /**
   * VOC_PJ-844: 払い運賃の内税フラグ（追加運賃と同方式）。true=内税、null/false=外税。
   */
  expenseIsTaxInclusive: nullable(boolean()),
  /**
   * VOC_PJ-888 #3917: 払い運賃の端数処理（矢羽根単位）。切上/切捨/四捨五入。
   * `null` のときは BE 側でカスケード fallback。内税フラグと対称化。
   */
  expenseRoundType: nullable(RoundTypePicklist),
  /**
   * 払い計上日（VOC_PJ-55）。
   * DB は CF-21 で Nullable のまま (SCHEDULED/FREE_TIME 等で null になりうる) だが、
   * UI 上は必須項目として扱う (業務要件)
   */
  expenseRecordingDate: nonNullableDateToStringSchema,
  orderNo: string(VALIDATION_MESSAGES.REQUIRED_ITEM),
  isUpdateOrderNo: boolean(),
  phoneNumber: nullable(string()), // ドライバーの電話番号
  /** 発送日 */
  groupShippingDate: nonNullableDateToStringSchema,
  /** 着日 */
  groupDeliveryDate: nonNullableDateToStringSchema,
  /**
   * VOC_PJ-29,431 #1857 / #3541 指摘-8: 支払側付帯費用 (対 パートナー)。
   * DispatchDetail (矢羽根) 単位で 1 セット (払い運賃 expense と同粒度)。
   * 混載 (1 矢羽根に複数配送依頼) でも 1 つだけ。root 直下に持つ。
   */
  additionalPayments: array(ShipmentOrderAdditionalFormValueSchema),
  /**
   * VOC_PJ-55 v5: groupモード専用。荷主単位の請求側付帯費用の一括入力行。
   * 並びは荷主グルーピング(groupFlatListByCustomer)と同順。single/childでは常に空配列(未使用)。
   * 入力があれば doGroupUpdate で荷主配下の全SOへ「同じ項目セット・金額按分」で展開する。
   * base スキーマに置く理由: フォーム型 `UpdateDispatchAndShipmentFormValue` を1本に保つため
   * (single/child は defaultValues で `[]` を渡し UI も送信も使わない)。
   */
  customerBulkAdditionalCharges: array(
    object({
      /** DispatchGroupCustomerBulkSection の荷主キー(customer-${id})と対応 */
      customerKey: string(),
      rows: array(ShipmentOrderAdditionalFormValueSchema)
    })
  )
}

/**
 * DispatchDetail と ShipmentOrder をまとめて更新するフォーム用スキーマ。
 * 単一矢羽根(single/child)モード。払い側の税区分・端数処理は必須。
 */
export const updateDispatchAndShipmentFormValueSchema =
  withExpenseTaxRequiredChecks(
    object({
      ...dispatchDetailRootEntries,
      // --- ShipmentOrder用(複数) ---
      shipments: array(shipmentItemWithIncomeRequiredSchema)
    })
  )

export type UpdateDispatchAndShipmentFormValue = InferOutput<
  typeof updateDispatchAndShipmentFormValueSchema
>

/**
 * groupモード(親矢羽根の複数配送依頼一括編集)専用のスキーマ変形。
 *
 * VOC_PJ-55 v4: 払い計上日(expenseRecordingDate)は「read-only現在値 + 常に空スタートの入力欄」
 * 方式に変更したため、groupモードでは未入力(null)のまま保存できる必要がある
 * (空 = 変更しない。全detail据え置き)。root entries を流用し、expenseRecordingDate だけ nullable にする。
 *
 * フォーム型は既存の `UpdateDispatchAndShipmentFormValue`(expenseRecordingDate: string)をそのまま使う。
 * nullable化は受理する入力を広げるだけで、groupモードの送信側(doGroupUpdate)は
 * `formValue.expenseRecordingDate != null` を分岐条件にしており、型上 string 扱いのままで実害がない。
 *
 * root の払い側必須チェック(expenseTaxCategoryId/expenseRoundType)は意図的に適用しない。
 * groupモードの払い側は「未入力=既存値を変更しない」持ち回りのため、必須化すると空スタートの
 * 入力欄で保存が塞がる。
 *
 * 請求側の同種の必須チェック(incomeTaxCategoryId/incomeRoundType)は配送依頼1件ごとのスキーマ側に付いており
 * entries 経由でそのまま流用されてしまうため、必須を持たない shipmentItemSchema で明示的に上書きする。
 * groupモードは請求側も「未入力=既存値を変更しない」持ち回りで、これらの入力欄自体を画面に出していないため、
 * 必須のままだとどこにもエラーを表示できないまま保存だけが塞がる。
 */
export const updateGroupDispatchAndShipmentFormValueSchema = object({
  ...dispatchDetailRootEntries,
  expenseRecordingDate: nullable(nonNullableDateToStringSchema),
  shipments: array(shipmentItemSchema)
})
